import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { categoryService } from '../services/api';
import { useAutoTranslate } from '../hooks/useAutoTranslate';
import { useAuth } from '../contexts/AuthContext';

interface Category {
  id: number;
  name: string;
  description: string;
  name_en?: string;
  description_en?: string;
  parent_id: number | null;
  parent_name: string | null;
  is_active: boolean;
  sort_order?: number;
  created_at: string;
  updated_at: string;
}

// Colore deterministico per l'avatar in base al nome
const AVATAR_COLORS = [
  ['#fef3c7', '#b45309'], ['#dbeafe', '#1d4ed8'], ['#dcfce7', '#15803d'],
  ['#fce7f3', '#be185d'], ['#ede9fe', '#6d28d9'], ['#ffedd5', '#c2410c'],
  ['#cffafe', '#0e7490'], ['#fee2e2', '#b91c1c'],
];
const catColor = (name: string) => {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
};

const PageContainer = styled.div`
  max-width: 1100px;
  margin: 0 auto;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
  flex-wrap: wrap;
`;

const PageSubtitle = styled.p`
  font-size: 14px;
  color: #94a3b8;
  margin: 4px 0 0;
`;

const NewButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #111827;
  color: white;
  border: none;
  border-radius: 12px;
  padding: 11px 18px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(17,24,39,0.2);
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  &:hover { transform: translateY(-1px); box-shadow: 0 8px 20px rgba(17,24,39,0.28); }
`;

const SearchBox = styled.div`
  position: relative;
  margin-bottom: 22px;
  max-width: 460px;
`;
const SearchField = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 12px 16px 12px 42px;
  border: 1px solid #e7ebf0;
  border-radius: 14px;
  background: #fff;
  font-size: 14px;
  color: #0f172a;
  transition: border-color 0.2s, box-shadow 0.2s;
  &:focus { outline: none; border-color: #cc9d6d; box-shadow: 0 0 0 4px rgba(204,157,109,0.12); }
  &::placeholder { color: #aab2bd; }
`;
const SearchIcon = styled.span`
  position: absolute;
  left: 15px; top: 50%; transform: translateY(-50%);
  color: #aab2bd; font-size: 16px; pointer-events: none;
`;

const Avatar = styled.div<{ $bg: string; $fg: string; $small?: boolean }>`
  width: ${p => p.$small ? 34 : 44}px;
  height: ${p => p.$small ? 34 : 44}px;
  border-radius: 13px;
  background: ${p => p.$bg};
  color: ${p => p.$fg};
  display: flex; align-items: center; justify-content: center;
  font-weight: 800;
  font-size: ${p => p.$small ? 14 : 17}px;
  flex-shrink: 0;
  text-transform: uppercase;
`;

const StatusPill = styled.span<{ $on: boolean }>`
  display: inline-flex; align-items: center; gap: 6px;
  font-size: 12px; font-weight: 600;
  color: ${p => p.$on ? '#15803d' : '#94a3b8'};
  &::before {
    content: '';
    width: 7px; height: 7px; border-radius: 50%;
    background: ${p => p.$on ? '#22c55e' : '#cbd5e1'};
  }
`;

const ActionBtn = styled.button<{ $danger?: boolean }>`
  min-width: 84px;
  height: 32px;
  padding: 0 14px;
  border-radius: 8px;
  border: 1px solid ${p => p.$danger ? '#fecaca' : '#e2e8f0'};
  background: #fff;
  color: ${p => p.$danger ? '#dc2626' : '#475569'};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  display: inline-flex; align-items: center; justify-content: center;
  transition: background 0.15s, border-color 0.15s;
  &:hover {
    background: ${p => p.$danger ? '#fef2f2' : '#f8fafc'};
    border-color: ${p => p.$danger ? '#fca5a5' : '#cbd5e1'};
  }
`;

const Title = styled.h1`
  font-size: 26px;
  font-weight: 800;
  color: #0f172a;
  margin: 0;
  letter-spacing: -0.5px;
`;

const Card = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
  border: 1px solid #eef2f7;
  width: 100%;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
  flex-shrink: 0;
  margin-left: auto;
`;

const CatEn = styled.span`
  font-size: 13px;
  color: #9ca3af;
  font-weight: 400;
`;

// ─── Vista raggruppata: card principale con sottocategorie dentro ───────────
const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
  gap: 18px;
  align-items: start;
`;

const ParentCard = styled.div<{ $active: boolean }>`
  background: white;
  border: 1px solid #eef2f7;
  border-radius: 18px;
  box-shadow: 0 2px 10px rgba(15, 23, 42, 0.05);
  overflow: hidden;
  opacity: ${p => p.$active ? 1 : 0.65};
  transition: box-shadow 0.2s ease, transform 0.2s ease;
  &:hover { box-shadow: 0 14px 32px rgba(15, 23, 42, 0.1); transform: translateY(-2px); }
`;

const ParentHead = styled.div`
  display: flex;
  align-items: center;
  gap: 13px;
  padding: 16px 18px;
  border-bottom: 1px solid #f1f5f9;
`;

const HeadMain = styled.div`flex: 1; min-width: 0;`;
const HeadName = styled.div`
  font-size: 16px; font-weight: 800; color: #0f172a;
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
`;
const HeadDesc = styled.div`font-size: 12.5px; color: #94a3b8; margin-top: 2px;`;
const CountBadge = styled.span`
  font-size: 11px; font-weight: 700; color: #64748b;
  background: #f1f5f9; border-radius: 20px; padding: 2px 9px;
`;

const SubList = styled.div`
  display: flex;
  flex-direction: column;
  padding: 6px;
`;
const SubRow = styled.div<{ $active: boolean }>`
  display: flex; align-items: center; gap: 10px;
  padding: 9px 12px; border-radius: 12px;
  opacity: ${p => p.$active ? 1 : 0.6};
  transition: background 0.15s;
  &:hover { background: #f8fafc; }
`;
const SubDot = styled.span<{ $bg: string }>`
  width: 9px; height: 9px; border-radius: 50%; flex-shrink: 0;
  background: ${p => p.$bg};
`;
const SubName = styled.span`flex: 1; min-width: 0; font-size: 14px; font-weight: 600; color: #334155;`;

const EmptySubs = styled.div`
  padding: 14px 16px; font-size: 13px; color: #b6bfca; font-style: italic;
`;

const AddSubBtn = styled.button`
  width: 100%;
  display: flex; align-items: center; justify-content: center; gap: 6px;
  background: none; border: none; border-top: 1px dashed #e7ebf0;
  color: #94a3b8; font-size: 13px; font-weight: 600; cursor: pointer;
  padding: 11px; transition: color 0.15s, background 0.15s;
  &:hover { color: #cc9d6d; background: #fffaf3; }
`;

const Modal = styled.div.withConfig({
  shouldForwardProp: (prop) => !['isOpen'].includes(prop),
})<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${({ isOpen }) => (isOpen ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: between;
  align-items: center;
  margin-bottom: 24px;
`;

const ModalTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #6b7280;
  padding: 0;
  
  &:hover {
    color: #374151;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FormActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: #374151;
`;

const Select = styled.select`
  padding: 12px 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 15px;
  background-color: white;
  color: #374151;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Checkbox = styled.input`
  margin: 0;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 48px 24px;
  color: #6b7280;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 48px 24px;
  color: #6b7280;
`;

const ErrorMessage = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 16px;
`;

const Categories: React.FC = () => {
  const { user } = useAuth();
  const canEdit = user?.role === 'admin' || user?.role === 'cook';
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    name_en: '',
    description_en: '',
    parent_id: '',
    is_active: true,
    sort_order: '0'
  });
  const [formErrors, setFormErrors] = useState<{[key: string]: string}>({});
  const [submitting, setSubmitting] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  // Hook per la traduzione automatica del nome
  const { translatedText: translatedName } = useAutoTranslate(
    formData.name, 
    formData.name_en
  );

  // Hook per la traduzione automatica della descrizione
  const { translatedText: translatedDescription } = useAutoTranslate(
    formData.description, 
    formData.description_en
  );

  // Aggiorna automaticamente i campi inglesi se vuoti e c'è una traduzione
  useEffect(() => {
    if (translatedName && !formData.name_en.trim()) {
      setFormData(prev => ({ ...prev, name_en: translatedName }));
    }
  }, [translatedName, formData.name_en]);

  useEffect(() => {
    if (translatedDescription && !formData.description_en.trim()) {
      setFormData(prev => ({ ...prev, description_en: translatedDescription }));
    }
  }, [translatedDescription, formData.description_en]);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await categoryService.getAll();
      setCategories(response.data.categories || []);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Errore nel caricamento delle categorie');
    } finally {
      setLoading(false);
    }
  };


  const openCreateModal = (parentId?: number) => {
    setEditingCategory(null);
    setFormData({
      name: '',
      description: '',
      name_en: '',
      description_en: '',
      parent_id: parentId ? String(parentId) : '',
      is_active: true,
      sort_order: '0'
    });
    setFormErrors({});
    setIsModalOpen(true);
  };

  const openEditModal = (category: Category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      description: category.description,
      name_en: category.name_en || '',
      description_en: category.description_en || '',
      parent_id: category.parent_id?.toString() || '',
      is_active: category.is_active,
      sort_order: (category.sort_order ?? 0).toString()
    });
    setFormErrors({});
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingCategory(null);
    setFormData({ 
      name: '', 
      description: '', 
      name_en: '',
      description_en: '',
      parent_id: '', 
      is_active: true,
      sort_order: '0'
    });
    setFormErrors({});
  };

  const validateForm = () => {
    const errors: {[key: string]: string} = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Il nome è obbligatorio';
    }

    // Verifica che non si stia creando una dipendenza circolare
    if (editingCategory && parseInt(formData.parent_id) === editingCategory.id) {
      errors.parent_id = 'Una categoria non può essere genitore di se stessa';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    try {
      setSubmitting(true);
      
      // Prepara i dati nel formato corretto per il backend
      const dataToSend = {
        name: formData.name.trim(),
        description: formData.description,
        description_en: formData.description_en,
        name_en: formData.name_en.trim(),
        parent_id: formData.parent_id ? parseInt(formData.parent_id) : null,
        is_active: formData.is_active,
        sort_order: Number.isNaN(parseInt(formData.sort_order)) ? 0 : parseInt(formData.sort_order)
      };
      
      if (editingCategory) {
        await categoryService.update(editingCategory.id, dataToSend);
      } else {
        await categoryService.create(dataToSend);
      }
      
      await fetchCategories();
      closeModal();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Errore nel salvataggio');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteRequest = (id: number) => {
    const hasChildren = categories.some(cat => cat.parent_id === id);
    
    if (hasChildren) {
      setError('Impossibile eliminare una categoria che ha sottocategorie. Elimina prima le sottocategorie.');
      return;
    }
    
    setDeleteId(id);
  };

  const handleDeleteConfirmed = async () => {
    if (deleteId == null) return;
    try {
      await categoryService.delete(deleteId);
      await fetchCategories();
      setDeleteId(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Errore nell\'eliminazione');
      setDeleteId(null);
    }
  };

  // Ottieni le categorie disponibili come genitori (escludendo quella corrente e i suoi discendenti)
  const getAvailableParents = (): Category[] => {
    if (!editingCategory) {
      return categories.filter(cat => cat.parent_id === null); // Solo categorie root per nuove categorie
    }
    
    // Per le modifiche, escludi la categoria stessa e i suoi discendenti
    const getDescendants = (categoryId: number): number[] => {
      const descendants: number[] = [];
      const children = categories.filter(cat => cat.parent_id === categoryId);
      children.forEach(child => {
        descendants.push(child.id);
        descendants.push(...getDescendants(child.id));
      });
      return descendants;
    };
    
    const excludeIds = [editingCategory.id, ...getDescendants(editingCategory.id)];
    return categories.filter(cat => !excludeIds.includes(cat.id));
  };

  if (loading) {
    return (
      <PageContainer>
        <LoadingState>Caricamento categorie...</LoadingState>
      </PageContainer>
    );
  }

  const mainCount = categories.filter(c => !c.parent_id).length;
  const subCount = categories.filter(c => c.parent_id).length;

  // Raggruppamento: categorie principali ciascuna con le proprie sottocategorie
  const q = searchTerm.trim().toLowerCase();
  const matchesSearch = (c: Category) => !q ||
    (c.name || '').toLowerCase().includes(q) ||
    (c.name_en || '').toLowerCase().includes(q) ||
    (c.description || '').toLowerCase().includes(q);

  const childrenOf = (parentId: number) =>
    categories
      .filter(c => c.parent_id === parentId)
      .sort((a, b) => {
        const ao = a.sort_order ?? 0, bo = b.sort_order ?? 0;
        if (ao !== bo) return ao - bo;
        return a.name.localeCompare(b.name);
      });

  const rootCats = categories
    .filter(c => !c.parent_id)
    .sort((a, b) => {
      const ao = a.sort_order ?? 0, bo = b.sort_order ?? 0;
      if (ao !== bo) return ao - bo;
      return a.name.localeCompare(b.name);
    });

  const visibleRoots = rootCats
    .map(root => {
      const allKids = childrenOf(root.id);
      const rootHit = matchesSearch(root);
      const kids = rootHit ? allKids : allKids.filter(matchesSearch);
      return { root, kids, visible: rootHit || kids.length > 0, totalKids: allKids.length };
    })
    .filter(g => g.visible);

  return (
    <PageContainer>
      <HeaderRow>
        <div>
          <Title>Categorie</Title>
          <PageSubtitle>
            {mainCount} {mainCount === 1 ? 'principale' : 'principali'} · {subCount} {subCount === 1 ? 'sottocategoria' : 'sottocategorie'}
          </PageSubtitle>
        </div>
        {canEdit && (
          <NewButton onClick={() => openCreateModal()}>
            <span style={{ fontSize: 18, lineHeight: 1 }}>＋</span> Nuova categoria
          </NewButton>
        )}
      </HeaderRow>

      <SearchBox>
        <SearchIcon>🔍</SearchIcon>
        <SearchField
          placeholder="Cerca categorie..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </SearchBox>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      {visibleRoots.length === 0 ? (
        <Card>
          <EmptyState>
            {searchTerm ? 'Nessuna categoria trovata' : 'Nessuna categoria presente'}
          </EmptyState>
        </Card>
      ) : (
        <Grid>
          {visibleRoots.map(({ root, kids, totalKids }) => {
            const [bg, fg] = catColor(root.name);
            return (
              <ParentCard key={root.id} $active={root.is_active}>
                <ParentHead>
                  <Avatar $bg={bg} $fg={fg}>{root.name.charAt(0)}</Avatar>
                  <HeadMain>
                    <HeadName>
                      {root.name}
                      {totalKids > 0 && <CountBadge>{totalKids}</CountBadge>}
                      {!root.is_active && <StatusPill $on={false}>Inattiva</StatusPill>}
                    </HeadName>
                    {root.description && <HeadDesc>{root.description}</HeadDesc>}
                  </HeadMain>
                  {canEdit && (
                    <ActionButtons>
                      <ActionBtn onClick={() => openEditModal(root)}>Modifica</ActionBtn>
                      <ActionBtn $danger onClick={() => handleDeleteRequest(root.id)}>Elimina</ActionBtn>
                    </ActionButtons>
                  )}
                </ParentHead>

                {kids.length > 0 ? (
                  <SubList>
                    {kids.map(kid => {
                      const [, kfg] = catColor(kid.name);
                      return (
                        <SubRow key={kid.id} $active={kid.is_active}>
                          <SubDot $bg={kfg} />
                          <SubName>
                            {kid.name}
                            {kid.name_en && kid.name_en !== kid.name && (
                              <CatEn> ({kid.name_en})</CatEn>
                            )}
                          </SubName>
                          {!kid.is_active && <StatusPill $on={false}>Off</StatusPill>}
                          {canEdit && (
                            <ActionButtons>
                              <ActionBtn onClick={() => openEditModal(kid)}>Modifica</ActionBtn>
                              <ActionBtn $danger onClick={() => handleDeleteRequest(kid.id)}>Elimina</ActionBtn>
                            </ActionButtons>
                          )}
                        </SubRow>
                      );
                    })}
                  </SubList>
                ) : (
                  <EmptySubs>Nessuna sottocategoria</EmptySubs>
                )}

                {canEdit && (
                  <AddSubBtn onClick={() => openCreateModal(root.id)}>
                    ＋ Aggiungi sottocategoria
                  </AddSubBtn>
                )}
              </ParentCard>
            );
          })}
        </Grid>
      )}

      <Modal isOpen={isModalOpen}>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>
              {editingCategory ? 'Modifica Categoria' : 'Nuova Categoria'}
            </ModalTitle>
            <CloseButton onClick={closeModal}>×</CloseButton>
          </ModalHeader>
          
          <Form onSubmit={handleSubmit}>
            <Input
              label="Nome"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              error={formErrors.name}
              required
              fullWidth
            />
            
            <Input
              label="Nome (Inglese)"
              value={formData.name_en}
              onChange={(e) => setFormData({ ...formData, name_en: e.target.value })}
              placeholder="Lascia vuoto per traduzione automatica"
              fullWidth
            />
            
            <Input
              label="Descrizione"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              error={formErrors.description}
              fullWidth
            />
            
            <Input
              label="Descrizione (Inglese)"
              value={formData.description_en}
              onChange={(e) => setFormData({ ...formData, description_en: e.target.value })}
              placeholder="Lascia vuoto per traduzione automatica"
              fullWidth
            />
            
            <FormGroup>
              <Label>Categoria Genitore</Label>
              <Select
                value={formData.parent_id}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  parent_id: e.target.value 
                })}
              >
                <option value="">Categoria principale</option>
                {getAvailableParents().map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </Select>
              {formErrors.parent_id && (
                <span style={{ color: '#dc2626', fontSize: '13px' }}>
                  {formErrors.parent_id}
                </span>
              )}
            </FormGroup>

            <Input
              label="Ordine"
              type="number"
              value={formData.sort_order}
              onChange={(e) => setFormData({ ...formData, sort_order: e.target.value })}
              placeholder="Imposta l'ordine delle categorie principali"
              fullWidth
            />
            
            <FormGroup>
              <CheckboxContainer>
                <Checkbox
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    is_active: e.target.checked 
                  })}
                />
                <Label htmlFor="is_active">Categoria attiva</Label>
              </CheckboxContainer>
            </FormGroup>
            
            <FormActions>
              <Button
                type="button"
                variant="secondary"
                onClick={closeModal}
                disabled={submitting}
              >
                Annulla
              </Button>
              <Button
                type="submit"
                loading={submitting}
                disabled={submitting}
              >
                {editingCategory ? 'Aggiorna' : 'Crea'}
              </Button>
            </FormActions>
          </Form>
        </ModalContent>
      </Modal>
      <ConfirmDialog
        isOpen={deleteId != null}
        title="Elimina categoria"
        message="Sei sicuro di voler eliminare questa categoria?"
        confirmLabel="Elimina"
        cancelLabel="Annulla"
        onConfirm={handleDeleteConfirmed}
        onCancel={() => setDeleteId(null)}
      />
    </PageContainer>
  );
};

export default Categories;
